import { ReactNode } from '@tanstack/react-router';
import { useFormContext } from 'react-hook-form';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { FormCounterField, FormInputField, lt } from '@mews/framework';
import {
    DropzoneField,
    FormLanguageProvider,
    LocalizedFormInputFields,
    LocalizedFormTextareaFields,
    SelectField,
} from '@mews-commander/form';
import { ContentSection } from '@mews-commander/ui';
import { getSortedLanguages } from '@mews-commander/legacy-entities/src/ducks/languages';
import { useCommanderSelector } from '@mews-commander/utils';
import { useProductCategoryOptions } from '../../queries/useProductCategoryOptions';
import { useCurrentAccountingEnvironment } from '../../hooks/useCurrentAccountingEnvironment';
import { ProductFormValues } from '../schemas/productFormSchema';
import { DetailsFormValues } from '../schemas/detailsSchema';

interface Props {
    helpers: Partial<Record<keyof DetailsFormValues, ReactNode>>;
    enabledExternalName?: boolean;
    enabledUnits?: boolean;
}

export const DetailsStepFields = ({ helpers, enabledUnits = true, enabledExternalName = true }: Props) => {
    const { trigger } = useFormContext<ProductFormValues>();
    const { defaultLanguageCode } = useCurrentEnterprise();
    const languages = useCommanderSelector(getSortedLanguages);

    const productCategoryOptions = useProductCategoryOptions();

    const accountingEnvironment = useCurrentAccountingEnvironment();

    return (
        <FormLanguageProvider defaultLanguageCode={defaultLanguageCode} languages={languages}>
            <ContentSection title={lt('DetailPlural')} spacing="300" contentSpacing="200">
                <LocalizedFormInputFields required autoFocus name="names" label={lt('Name')} helper={helpers.names} />
                <LocalizedFormInputFields name="shortNames" label={lt('ShortName')} />
                {enabledExternalName ? (
                    <LocalizedFormInputFields label={lt('ExternalName')} name="externalNames" />
                ) : null}
                <LocalizedFormTextareaFields
                    helper={helpers.descriptions}
                    label={lt('Description')}
                    name="descriptions"
                />
                {enabledUnits && accountingEnvironment.useBillingUnits ? (
                    <LocalizedFormInputFields required name="units" label={lt('UnitPlural')} />
                ) : null}
                <SelectField
                    name="productCategoryId"
                    label={lt('Category')}
                    options={productCategoryOptions}
                    emptyValueAsNull
                />
                <FormCounterField label={lt('Ordering')} name="ordering" helper={lt('OrderingHint')} />
                <DropzoneField
                    name="image"
                    label={lt('Image')}
                    accept=".png,.jpg,.jpeg"
                    helper={lt('ProductFormInputHintImage')}
                    onChange={async () => {
                        await trigger('image');
                    }}
                />
            </ContentSection>
            <ContentSection title={lt('Mapping')} spacing="300">
                <FormInputField
                    label={lt('ExternalIdentifier')}
                    name="externalIdentifier"
                    helper={helpers.externalIdentifier}
                />
            </ContentSection>
        </FormLanguageProvider>
    );
};
